import { globalStyle, keyframes, style } from '@vanilla-extract/css';
import { type RecipeVariants, recipe } from '@vanilla-extract/recipes';

import rootStyles from '../rootElement/Root.css';
import { getVar, themeVar } from '../themes/theme';

const { rootElement } = rootStyles;

export const buttonElement = style({
  position: 'relative',
  display: 'flex',
  justifyContent: 'var(--horizontal-alignment)',
  width: 'var(--width)',
  maxWidth: 'var(--max-width)',
  margin: 'var(--margin)',
  selectors: {
    [`.ProseMirror > .react-renderer > &, ${rootElement} > &`]: {
      paddingLeft: 30,
      paddingRight: 30,
    },
  },
});

export const themeableButton = recipe({
  base: {
    // use `buttonElement` as the base styles
  },
  variants: {
    variant: {
      primary: {
        background: getVar(themeVar.color.button.primary.background),
        boxShadow: getVar(themeVar.shadow.button.primary),
        borderRadius: getVar(themeVar.radius.button.primary),
        border: getVar(themeVar.border.button.primary),
      },
      secondary: {
        background: getVar(themeVar.color.button.secondary.background),
        boxShadow: getVar(themeVar.shadow.button.secondary),
        borderRadius: getVar(themeVar.radius.button.secondary),
        border: getVar(themeVar.border.button.secondary),
        color: getVar(themeVar.color.button.secondary.text, 'unset'),
      },
    },
  },
  defaultVariants: {
    variant: 'primary',
  },
});

export type ButtonContainer = RecipeVariants<typeof themeableButton>;

export const button = style({
  position: 'relative',
  padding: '0.625rem',
  margin: '10px 0px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  fontFamily: 'var(--font-family, inherit)',
  fontSize: 'var(--font-size)',
  fontWeight: 'var(--font-weight, 600)',
  backgroundColor: 'var(--button-background-color)',
  borderColor: 'var(--border-color)',
  borderWidth: 'var(--border-width)',
  borderStyle: 'var(--border-style)',
  borderRadius: 'var(--border-radius)',
  // font settings will most likely be overriden by leaf
  background: 'var(--button-background-color)',
  color: 'var(--text-color)',
  lineHeight: 'unset',

  '@media': {
    'only screen and (--mobile)': {
      fontSize: 'var(--mobile-font-size, var(--font-size, inherit))',
    },
  },

  selectors: {
    [`${rootElement} a &, &[type="submit"]`]: {
      cursor: 'pointer',
    },
    '&:disabled': {
      opacity: '50%',
    },
    '&:hover': {
      backgroundColor: 'var(--hover-background-color)',
      color: 'var(--hover-text-color)',
      borderColor: 'var(--hover-border-color)',
      borderStyle: 'var(--hover-border-style)',
      borderWidth: 'var(--hover-border-width)',
      borderRadius: 'var(--hover-border-radius)',
    },
  },
});

export const icon = style({
  display: 'grid',
  placeItems: 'center',
  textAlign: 'center',
  verticalAlign: 'middle',
  lineHeight: '20px',
  cursor: 'pointer',
  width: 'fit-content',
  pointerEvents: 'none',
  boxSizing: 'content-box',
  fontSize: '16px',
  color: 'var(--icon-color)',
  selectors: {
    '[data-dream-editor] &': {
      pointerEvents: 'initial',
    },
    '[data-dream-editor] &:hover': {
      borderRadius: 'var(--border-radius)',
      backgroundColor: 'rgba(0,0,0,.1)',
    },
    '&[data-placement="left"]:not([data-no-text="true"])': {
      marginRight: '0.5rem',
    },
    '&[data-placement="right"]:not([data-no-text="true"])': {
      marginLeft: '0.5rem',
    },
    [`${button}:hover &`]: {
      color: 'var(--hover-icon-color)',
    },
  },
});

globalStyle(`${icon} img`, {
  height: 20,
  width: 'auto',
});

export const glowingButton = style({
  position: 'relative',
  borderRadius: 'var(--border-radius)',
  border: 'none',
  outline: 'none',
});

export const wrapper = style({
  position: 'absolute',
  inset: '1px',
  borderRadius: 'var(--border-radius)',
  transition: 'opacity 0.3s ease-in',
  opacity: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  userSelect: 'none',
  pointerEvents: 'none',
});

export const wrapperBackground = recipe({
  base: {},
  variants: {
    glowing_background: {
      black: {
        background: '#1b1b1b',
        color: '#fff',
      },
      white: {
        background: '#fff',
        color: '#1b1b1b',
      },
    },
  },
  defaultVariants: {
    glowing_background: 'black',
  },
});

export const glowWrapper = style({
  position: 'absolute',
  width: '100%',
  height: '100%',
  borderRadius: 'var(--border-radius)',
  overflow: 'hidden',
  userSelect: 'none',
  pointerEvents: 'none',
});

export const content = style({
  width: 'fit-content',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  userSelect: 'none',
  zIndex: '1',
});

export const rotatingBorder = style({
  position: 'absolute',
  left: 0,
  opacity: 1,
  userSelect: 'none',
  pointerEvents: 'none',
});

export const rotatingShadow = style({
  position: 'absolute',
  left: 0,
  borderRadius: '999px',
  userSelect: 'none',
  pointerEvents: 'none',
});

export const keyframeRotateAnimation = keyframes({
  '0%': {
    transform: 'translateX(var(--start-position)) scaleX(1)',
  },
  '25%': {
    transform: 'translateX(var(--mid-top-position)) scaleX(var(--half-width))',
  },
  '35%': {
    transform: 'translateX(var(--end-position)) scaleX(1)',
  },
  '50%': {
    transform: 'translateX(var(--end-position)) rotate(180deg) scaleX(1)',
  },
  '75%': {
    transform: 'translateX(var(--mid-bottom-position)) rotate(180deg) scaleX(var(--half-width))',
  },
  '85%': {
    transform: 'translateX(var(--start-position))  rotate(180deg) scaleX(1)',
  },
  '100%': {
    transform: 'translateX(var(--start-position)) rotate(360deg) scaleX(1)',
  },
});

export const keyframeSmallRotateAnimation = keyframes({
  '0%': {
    transform: 'translateX(0px)',
  },
  '25%': {
    transform: 'translateX(var(--end-position))',
  },
  '50%': {
    transform: 'translateX(var(--end-position)) rotate(180deg)',
  },
  '75%': {
    transform: 'translateX(0px) rotate(180deg)',
  },
  '100%': {
    transform: 'translateX(0px) rotate(360deg)',
  },
});

export const keyframeRotateAnimationBorder = keyframes({
  '0%': {
    transform: 'translateX(0px) scaleX(1)',
  },
  '33.33%': {
    transform: 'translateX(var(--button-width)) scaleX(var(--width-multiplier))',
  },
  '50%': {
    transform: 'translateX(var(--button-width)) rotate(180deg) scaleX(1)',
  },
  '83.33%': {
    transform: 'translateX(0px) rotate(180deg) scaleX(var(--width-multiplier))',
  },
  '100%': {
    transform: 'translateX(0px) rotate(360deg) scaleX(1)',
  },
});

export const keyframeRotateAnimationCircle = keyframes({
  '0%': {
    transform: 'translateX(2px)',
  },
  '50%': {
    transform: 'rotate(180deg)',
  },
  '100%': {
    transform: 'rotate(360deg)',
  },
});

export const rotateAnimation = style({
  animation: `${keyframeRotateAnimation} 3s linear infinite`,
});

export const smallRotateAnimation = style({
  animation: `${keyframeSmallRotateAnimation} 3s linear infinite`,
});

export const rotateAnimationBorder = style({
  animation: `${keyframeRotateAnimationBorder} 3s linear infinite`,
});

export const rotateAnimationCircle = style({
  animation: `${keyframeRotateAnimationCircle} 3s linear infinite`,
});
